/**
 * ServerCapabilitiesPanel — Grille des 7 capabilities + CTA rebuild si workspaces_ready=false.
 *
 * @spec specs/app/admin/dedicated-servers.md §6.2
 */

import { Alert, Button, Tooltip } from 'antd';
import { CheckCircle2, Clock, RotateCcw, XCircle } from 'lucide-react';
import type { FC } from 'react';
import {
  CAPABILITY_LABELS_FR,
  CAPABILITY_TOOLTIPS_FR,
  type ServerCapabilities,
} from '@/types/dedicatedServer';

interface ServerCapabilitiesPanelProps {
  capabilities?: ServerCapabilities | null;
  onRebuildClick?: () => void;
}

const CAPABILITY_ORDER: Array<keyof ServerCapabilities> = [
  'docker_ready',
  'tls_ready',
  'sysbox_ready',
  'redis_ready',
  'firewall_ready',
  'workspaces_ready',
  'log_streaming_ready',
];

const ServerCapabilitiesPanel: FC<ServerCapabilitiesPanelProps> = ({
  capabilities,
  onRebuildClick,
}) => {
  if (!capabilities) {
    return (
      <div className="fc-card p-5">
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Capabilities
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Capabilities non disponibles pour ce serveur.
        </p>
      </div>
    );
  }

  const readyCount = CAPABILITY_ORDER.filter((k) => capabilities[k]).length;
  const showRebuildAlert = capabilities.workspaces_ready === false && onRebuildClick;

  return (
    <div className="fc-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Capabilities
        </h3>
        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
          {readyCount}/{CAPABILITY_ORDER.length} OK
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {CAPABILITY_ORDER.map((key) => {
          const ready = capabilities[key];
          const isWorkspacesPending = key === 'workspaces_ready' && !ready;
          return (
            <Tooltip key={key} title={CAPABILITY_TOOLTIPS_FR[key]}>
              <div
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  ready
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300'
                    : isWorkspacesPending
                      ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300'
                      : 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300'
                }`}
              >
                {ready ? (
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                ) : isWorkspacesPending ? (
                  <Clock className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 flex-shrink-0" />
                )}
                <span className="font-medium">{CAPABILITY_LABELS_FR[key]}</span>
              </div>
            </Tooltip>
          );
        })}
      </div>

      {showRebuildAlert && (
        <Alert
          className="mt-4"
          type="warning"
          showIcon
          message="Images workspace non prêtes"
          description="Les images Docker workspace n'ont pas été construites correctement. Une reconstruction est requise pour activer la création de workspaces."
          action={
            <Button
              type="primary"
              icon={<RotateCcw className="h-3.5 w-3.5" />}
              onClick={onRebuildClick}
              size="small"
            >
              Reconstruire les images
            </Button>
          }
        />
      )}
    </div>
  );
};

export default ServerCapabilitiesPanel;
